"use client"

import { useEffect, useState, useCallback, useMemo } from "react"
import { codeValueService } from "@/lib/code-value-service"
import type { CodeItem } from "@/types/code-values"

/**
 * Hook for working with code values
 * Provides the same functionality as the Angular CodeValueService
 */
export function useCodeValues() {
  const [isReady, setIsReady] = useState(false)

  // Check if the global code values are available
  useEffect(() => {
    // Check if already ready
    if (typeof window !== "undefined" && window.PhoenixCodeValues) {
      setIsReady(true)
      return
    }

    // If not ready, set up an interval to check periodically
    // But limit the number of attempts to prevent infinite polling
    let attempts = 0
    const maxAttempts = 10 // 5 seconds total (10 * 500ms)
    
    const interval = setInterval(() => {
      attempts++
      
      if (typeof window !== "undefined" && window.PhoenixCodeValues) {
        setIsReady(true)
        clearInterval(interval)
      } else if (attempts >= maxAttempts) { // Code values not loaded after maximum attempts, proceeding with default values
        setIsReady(true) // Set ready anyway to prevent infinite polling
        clearInterval(interval)
      }
    }, 500)

    return () => clearInterval(interval)
  }, [])

  // Memoize all methods to prevent recreation on each render
  const getCodeValueText = useCallback(
    (id: number, groupName: string): string => codeValueService.getCodeValueText(id, groupName),
    [],
  )

  const getCodeValueCode = useCallback(
    (id: number, groupName: string): string => codeValueService.getCodeValueCode(id, groupName),
    [],
  )

  const getCodeValueIdByCode = useCallback(
    (code: string, groupName: string): number | null => codeValueService.getCodeValueIdByCode(code, groupName),
    [],
  )

  const getCodeValueTextByCode = useCallback(
    (code: string, groupName: string): string => codeValueService.getCodeValueTextByCode(code, groupName),
    [],
  )

  const getCodeValue = useCallback(
    (id: number, groupName: string): CodeItem | null => codeValueService.getCodeValue(id, groupName),
    [],
  )

  const getCodeValueByCode = useCallback(
    (code: string, groupName: string): CodeItem | null => codeValueService.getCodeValueByCode(code, groupName),
    [],
  )

  const getParentId = useCallback(
    (groupName: string, id: number): number => codeValueService.getParentId(groupName, id),
    [],
  )

  const getRelatedCodeValues = useCallback(
    (groupName: string, parentId: any, parentGroup: string): Array<CodeItem> =>
      codeValueService.getRelatedCodeValues(groupName, parentId, parentGroup),
    [],
  )

  const getCodeValues = useCallback(
    (groupName: string, required: boolean): Array<CodeItem> => codeValueService.getCodeValues(groupName, required),
    [],
  )

  const getCodeValuesSortByCode = useCallback(
    (groupName: string, required: boolean): Array<CodeItem> =>
      codeValueService.getCodeValuesSortByCode(groupName, required),
    [],
  )

  const getCodeValuesSortByText = useCallback(
    (groupName: string, required: boolean): Array<CodeItem> =>
      codeValueService.getCodeValuesSortByText(groupName, required),
    [],
  )

  const getCodeValueGroup = useCallback(
    (groupName: string): Array<CodeItem> => codeValueService.getCodeValueGroup(groupName),
    [],
  )

  // Return memoized object to prevent recreation on each render
  return useMemo(
    () => ({
      isReady,
      getCodeValueText,
      getCodeValueCode,
      getCodeValueIdByCode,
      getCodeValueTextByCode,
      getCodeValue,
      getCodeValueByCode,
      getParentId,
      getRelatedCodeValues,
      getCodeValues,
      getCodeValuesSortByCode,
      getCodeValuesSortByText,
      getCodeValueGroup,
    }),
    [
      isReady,
      getCodeValueText,
      getCodeValueCode,
      getCodeValueIdByCode,
      getCodeValueTextByCode,
      getCodeValue,
      getCodeValueByCode,
      getParentId,
      getRelatedCodeValues,
      getCodeValues,
      getCodeValuesSortByCode,
      getCodeValuesSortByText,
      getCodeValueGroup,
    ],
  )
}
